/**
 * TransactionColumnSelector
 *
 * A dropdown that lets users choose which optional transaction details appear
 * in the dense adaptive list. Preferences persist in localStorage.
 *
 * [T65P0] Customizable column display for transaction history
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button, type ButtonProps } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

export type TransactionColumnId =
  | 'date'
  | 'description'
  | 'type'
  | 'category'
  | 'account'
  | 'amount'
  | 'notes'
  | 'tags'
  | 'recurring';

export interface ColumnDef {
  id: TransactionColumnId;
  label: string;
  detailLabel?: string;
  required?: boolean; // cannot be hidden
  defaultVisible: boolean;
}

export const TRANSACTION_COLUMNS: ColumnDef[] = [
  { id: 'date', label: 'Date', defaultVisible: true },
  { id: 'description', label: 'Description', detailLabel: 'Description', required: true, defaultVisible: true },
  { id: 'type', label: 'Type', defaultVisible: true },
  { id: 'category', label: 'Category', defaultVisible: true },
  { id: 'account', label: 'Account', defaultVisible: false },
  { id: 'amount', label: 'Amount', detailLabel: 'Amount', required: true, defaultVisible: true },
  { id: 'notes', label: 'Notes', defaultVisible: false },
  { id: 'tags', label: 'Tags', defaultVisible: false },
  { id: 'recurring', label: 'Recurring', defaultVisible: false },
];

const VISIBLE_DETAIL_TEST_IDS: Partial<Record<TransactionColumnId, string>> = {
  date: 'visible-detail-toggle-date',
  type: 'visible-detail-toggle-type',
  category: 'visible-detail-toggle-category',
  account: 'visible-detail-toggle-account',
  notes: 'visible-detail-toggle-notes',
  tags: 'visible-detail-toggle-tags',
  recurring: 'visible-detail-toggle-recurring',
};

const STORAGE_KEY = 'fintrack:txn-visible-columns';

function getDefaultVisibility(): Record<TransactionColumnId, boolean> {
  const vis: Record<string, boolean> = {};
  for (const col of TRANSACTION_COLUMNS) {
    vis[col.id] = col.defaultVisible;
  }
  return vis as Record<TransactionColumnId, boolean>;
}

function loadVisibility(): Record<TransactionColumnId, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Merge with defaults to cover new columns added after the saved snapshot
      const defaults = getDefaultVisibility();
      return { ...defaults, ...parsed };
    }
  } catch {
    /* ignore malformed */
  }
  return getDefaultVisibility();
}

// ---------------------------------------------------------------------------
// Hook — useTransactionColumns
// ---------------------------------------------------------------------------

export function useTransactionColumns() {
  const [visibility, setVisibility] = useState<Record<TransactionColumnId, boolean>>(loadVisibility);

  // Persist on change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visibility));
  }, [visibility]);

  const toggleColumn = useCallback((id: TransactionColumnId) => {
    setVisibility((prev) => {
      const col = TRANSACTION_COLUMNS.find((c) => c.id === id);
      if (col?.required) return prev; // cannot toggle required columns
      return { ...prev, [id]: !prev[id] };
    });
  }, []);

  const resetColumns = useCallback(() => {
    setVisibility(getDefaultVisibility());
  }, []);

  /**
   * Restore visibility to a previously-saved snapshot.
   * Use this instead of a toggle-loop when undoing a reset, to avoid
   * stale-closure issues where the captured `isVisible` still reads
   * from the pre-reset visibility object.
   */
  const restoreColumns = useCallback((snapshot: Record<TransactionColumnId, boolean>) => {
    setVisibility(snapshot);
  }, []);

  const isVisible = useCallback(
    (id: TransactionColumnId) => visibility[id] ?? false,
    [visibility],
  );

  const visibleColumns = useMemo(
    () => TRANSACTION_COLUMNS.filter((c) => visibility[c.id]),
    [visibility],
  );

  return { visibility, toggleColumn, resetColumns, restoreColumns, isVisible, visibleColumns };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TransactionColumnSelectorProps {
  visibility: Record<TransactionColumnId, boolean>;
  onToggle: (id: TransactionColumnId) => void;
  onReset: () => void;
  triggerClassName?: string;
  triggerVariant?: ButtonProps['variant'];
  align?: 'start' | 'center' | 'end';
  itemCountVisible?: boolean;
  onItemCountChange?: (visible: boolean) => void;
}

export default function TransactionColumnSelector({
  visibility,
  onToggle,
  onReset,
  triggerClassName,
  triggerVariant = 'ghost',
  align = 'start',
  itemCountVisible,
  onItemCountChange,
}: TransactionColumnSelectorProps) {
  const hasCustomisation = useMemo(() => {
    const defaults = getDefaultVisibility();
    const columnCustomisation = TRANSACTION_COLUMNS.some((c) => visibility[c.id] !== defaults[c.id]);
    const itemCountCustomisation = typeof itemCountVisible === 'boolean' && itemCountVisible !== true;
    return columnCustomisation || itemCountCustomisation;
  }, [visibility, itemCountVisible]);

  const visibleDetailOptions = useMemo(
    () => TRANSACTION_COLUMNS.filter((col) => !col.required),
    [],
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={hasCustomisation ? 'secondary' : triggerVariant}
          size="sm"
          className={cn(triggerClassName, hasCustomisation && 'border border-primary/40 bg-primary/10 text-primary hover:bg-primary/15')}
          data-testid="view-options-btn"
          aria-label="View Options"
          title="View Options"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          <span className="sm:hidden">View</span>
          <span className="hidden sm:inline">View Options</span>
          {hasCustomisation && (
            <Badge
              variant="outline"
              className="h-4 px-1.5 text-[10px] leading-none"
              data-testid="view-options-customized-badge"
            >
              Custom
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-56" data-testid="view-options-menu">
        <DropdownMenuLabel>Visible details</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {visibleDetailOptions.map((col) => (
          <DropdownMenuCheckboxItem
            key={col.id}
            checked={visibility[col.id]}
            onCheckedChange={() => onToggle(col.id)}
            onSelect={(e) => e.preventDefault()} // keep dropdown open
            data-testid={VISIBLE_DETAIL_TEST_IDS[col.id]}
          >
            {col.detailLabel ?? col.label}
          </DropdownMenuCheckboxItem>
        ))}
        {typeof itemCountVisible === 'boolean' && onItemCountChange && (
          <DropdownMenuCheckboxItem
            checked={itemCountVisible}
            onCheckedChange={(checked) => onItemCountChange(checked === true)}
            onSelect={(e) => e.preventDefault()}
            data-testid="visible-detail-toggle-item-count"
          >
            Item count
          </DropdownMenuCheckboxItem>
        )}
        {hasCustomisation && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onReset}
              className="text-muted-foreground"
              data-testid="visible-details-reset-btn"
            >
              Reset to default
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
